import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { fetchInterestedParties, fetchSwotEntries } from '../context/contextApi';
import { SCORE_OPTIONS } from '../context/scoringUtils';
import type { InterestedParty, SwotEntry } from '../context/types';
import { fetchEvaluationMethods } from './risksApi';
import { fetchRiskStudies, fetchStudyActivities } from '../riskStudies/riskStudiesApi';
import type { RiskStudy, StudyActivity } from '../riskStudies/types';
import { fetchCatalogItems } from '../riskCatalog/riskCatalogApi';
import type { RiskCatalogItem } from '../riskCatalog/types';
import type {
  EvaluationMethod,
  RiskEntry,
  RiskKind,
  RiskPayload,
  RiskScope,
  RiskTreatment,
} from './types';
import { RISK_KIND_LABEL, RISK_TREATMENT_LABEL } from './riskUtils';

const TREATMENTS: RiskTreatment[] = ['mitigar', 'aceptar', 'transferir', 'evitar', 'explotar'];

export interface RiskFormInitial {
  kind?: RiskKind;
  scope?: RiskScope;
  title?: string;
  description?: string;
  processArea?: string;
  sourceSwotId?: string | null;
  sourcePartyId?: string | null;
  studyId?: string | null;
  activityId?: string | null;
  evaluationMethodId?: string | null;
  probability?: number;
  impact?: number;
  treatment?: RiskTreatment;
}

interface RiskFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: RiskEntry | null;
  prefill?: RiskFormInitial;
  onSubmit: (payload: RiskPayload) => Promise<void>;
}

function defaultMethodId(methods: EvaluationMethod[]): string {
  return methods.find((m) => m.isDefault)?.id ?? methods[0]?.id ?? '';
}

export function RiskFormModal({
  open,
  onOpenChange,
  initial,
  prefill,
  onSubmit,
}: RiskFormModalProps) {
  const [kind, setKind] = useState<RiskKind>('riesgo');
  const [scope, setScope] = useState<RiskScope>('estrategico');
  const [studyId, setStudyId] = useState('');
  const [activityId, setActivityId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [processArea, setProcessArea] = useState('');
  const [sourceSwotId, setSourceSwotId] = useState<string>('');
  const [sourcePartyId, setSourcePartyId] = useState<string>('');
  const [evaluationMethodId, setEvaluationMethodId] = useState('');
  const [probability, setProbability] = useState(3);
  const [impact, setImpact] = useState(3);
  const [severity, setSeverity] = useState(3);
  const [occurrence, setOccurrence] = useState(3);
  const [detection, setDetection] = useState(3);
  const [residualSeverity, setResidualSeverity] = useState(2);
  const [residualOccurrence, setResidualOccurrence] = useState(2);
  const [residualDetection, setResidualDetection] = useState(2);
  const [residualControl, setResidualControl] = useState(3);
  const [treatment, setTreatment] = useState<RiskTreatment>('mitigar');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [swotOptions, setSwotOptions] = useState<SwotEntry[]>([]);
  const [partyOptions, setPartyOptions] = useState<InterestedParty[]>([]);
  const [methods, setMethods] = useState<EvaluationMethod[]>([]);
  const [studies, setStudies] = useState<RiskStudy[]>([]);
  const [activities, setActivities] = useState<StudyActivity[]>([]);
  const [managementAreas, setManagementAreas] = useState<RiskCatalogItem[]>([]);
  const [failureModes, setFailureModes] = useState<RiskCatalogItem[]>([]);
  const [effects, setEffects] = useState<RiskCatalogItem[]>([]);
  const [managementAreaId, setManagementAreaId] = useState('');
  const [failureModeId, setFailureModeId] = useState('');
  const [effectId, setEffectId] = useState('');

  const selectedMethod = useMemo(
    () => methods.find((m) => m.id === evaluationMethodId),
    [methods, evaluationMethodId],
  );

  const usesSod = selectedMethod?.kind === 'sod' || selectedMethod?.kind === 'sodc';

  useEffect(() => {
    if (!open) return;
    void Promise.all([
      fetchSwotEntries({ active: true }),
      fetchInterestedParties({ active: true }),
      fetchEvaluationMethods(),
    ]).then(([swot, parties, evalMethods]) => {
      setSwotOptions(swot);
      setPartyOptions(parties);
      setMethods(evalMethods);
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    void fetchRiskStudies().then(setStudies).catch(() => setStudies([]));
  }, [open]);

  useEffect(() => {
    if (!open || scope !== 'operativo') return;
    void Promise.all([
      fetchCatalogItems('area_gestion', true),
      fetchCatalogItems('modo_falla', true),
      fetchCatalogItems('efecto', true),
    ]).then(([areas, modes, effectItems]) => {
      setManagementAreas(areas);
      setFailureModes(modes);
      setEffects(effectItems);
    }).catch(() => {
      setManagementAreas([]);
      setFailureModes([]);
      setEffects([]);
    });
  }, [open, scope]);

  useEffect(() => {
    if (!open || !studyId) {
      setActivities([]);
      return;
    }
    void fetchStudyActivities(studyId).then(setActivities).catch(() => setActivities([]));
  }, [open, studyId]);

  useEffect(() => {
    if (!open) return;
    setKind(initial?.kind ?? prefill?.kind ?? 'riesgo');
    setScope(initial?.scope ?? prefill?.scope ?? 'estrategico');
    setStudyId(initial?.studyId ?? prefill?.studyId ?? '');
    setActivityId(initial?.activityId ?? prefill?.activityId ?? '');
    setTitle(initial?.title ?? prefill?.title ?? '');
    setDescription(initial?.description ?? prefill?.description ?? '');
    setProcessArea(initial?.processArea ?? prefill?.processArea ?? '');
    setSourceSwotId(initial?.sourceSwotId ?? prefill?.sourceSwotId ?? '');
    setSourcePartyId(initial?.sourcePartyId ?? prefill?.sourcePartyId ?? '');
    setManagementAreaId('');
    setFailureModeId('');
    setEffectId('');
    setEvaluationMethodId(
      initial?.evaluationMethodId
        ?? initial?.evaluationMethod?.id
        ?? prefill?.evaluationMethodId
        ?? '',
    );
    setFormError(null);
    setProbability(initial?.probability ?? prefill?.probability ?? 3);
    setImpact(initial?.impact ?? prefill?.impact ?? 3);
    setTreatment(initial?.treatment ?? prefill?.treatment ?? 'mitigar');
    setResidualSeverity(initial?.residualImpact ?? 2);
    setResidualOccurrence(initial?.residualProbability ?? 2);
    setResidualDetection(2);
    setResidualControl(3);

    const initialAssessment = initial?.assessments?.find((a) => a.kind === 'inicial');
    if (initialAssessment) {
      setSeverity(initialAssessment.severity);
      setOccurrence(initialAssessment.occurrence);
      setDetection(initialAssessment.detection ?? 3);
    } else {
      setSeverity(initial?.impact ?? 3);
      setOccurrence(initial?.probability ?? 3);
      setDetection(3);
    }

    const residualAssessment = initial?.assessments?.find((a) => a.kind === 'residual');
    if (residualAssessment) {
      setResidualSeverity(residualAssessment.severity);
      setResidualOccurrence(residualAssessment.occurrence);
      setResidualDetection(residualAssessment.detection ?? 2);
      setResidualControl(residualAssessment.controlLevel ?? 3);
    }
  }, [open, initial, prefill]);

  useEffect(() => {
    if (!open || initial || methods.length === 0) return;
    setEvaluationMethodId((current) => current || defaultMethodId(methods));
  }, [open, initial, methods]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!evaluationMethodId) {
      setFormError('Seleccioná un método de evaluación.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const payload: RiskPayload = {
        kind,
        scope,
        title: title.trim(),
        description: description.trim(),
        processArea: processArea.trim() || null,
        sourceSwotId: sourceSwotId || null,
        sourcePartyId: sourcePartyId || null,
        evaluationMethodId: evaluationMethodId || null,
        studyId: scope === 'operativo' ? studyId || null : null,
        activityId: scope === 'operativo' ? activityId || null : null,
        managementAreaId: scope === 'operativo' ? managementAreaId || null : null,
        failureModeId: scope === 'operativo' ? failureModeId || null : null,
        effectId: scope === 'operativo' ? effectId || null : null,
        probability: usesSod ? occurrence : probability,
        impact: usesSod ? severity : impact,
        treatment,
      };

      if (!initial) {
        payload.severity = severity;
        payload.occurrence = occurrence;
        payload.detection = detection;
      }

      await onSubmit(payload);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{initial ? 'Editar riesgo/oportunidad' : 'Nuevo riesgo/oportunidad'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label htmlFor='risk-kind' className='block text-sm font-medium mb-1'>
                Tipo
              </label>
              <select
                id='risk-kind'
                value={kind}
                onChange={(e) => setKind(e.target.value as RiskKind)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                {(['riesgo', 'oportunidad'] as RiskKind[]).map((k) => (
                  <option key={k} value={k}>
                    {RISK_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor='risk-scope' className='block text-sm font-medium mb-1'>
                Ámbito
              </label>
              <select
                id='risk-scope'
                value={scope}
                onChange={(e) => setScope(e.target.value as RiskScope)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                <option value='estrategico'>Estratégico (F-GL-05)</option>
                <option value='operativo'>Operativo / IPER (FQ-22)</option>
              </select>
            </div>
            <div>
              <label htmlFor='risk-treatment' className='block text-sm font-medium mb-1'>
                Tratamiento
              </label>
              <select
                id='risk-treatment'
                value={treatment}
                onChange={(e) => setTreatment(e.target.value as RiskTreatment)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                {TREATMENTS.map((t) => (
                  <option key={t} value={t}>
                    {RISK_TREATMENT_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {scope === 'operativo' && (
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <label htmlFor='risk-study' className='block text-sm font-medium mb-1'>
                  Estudio IPER
                </label>
                <select
                  id='risk-study'
                  value={studyId}
                  onChange={(e) => {
                    setStudyId(e.target.value);
                    setActivityId('');
                  }}
                  required
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                >
                  <option value=''>— Seleccionar estudio —</option>
                  {studies.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='risk-activity' className='block text-sm font-medium mb-1'>
                  Actividad
                </label>
                <select
                  id='risk-activity'
                  value={activityId}
                  onChange={(e) => setActivityId(e.target.value)}
                  required
                  disabled={!studyId}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50'
                >
                  <option value=''>— Seleccionar actividad —</option>
                  {activities.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.outlineNo} {a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {scope === 'operativo' && (
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
              <div>
                <label htmlFor='risk-area' className='block text-sm font-medium mb-1'>
                  Área de gestión
                </label>
                <select
                  id='risk-area'
                  value={managementAreaId}
                  onChange={(e) => setManagementAreaId(e.target.value)}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                >
                  <option value=''>— Opcional —</option>
                  {managementAreas.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='risk-failure-mode' className='block text-sm font-medium mb-1'>
                  Modo de falla
                </label>
                <select
                  id='risk-failure-mode'
                  value={failureModeId}
                  onChange={(e) => setFailureModeId(e.target.value)}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                >
                  <option value=''>— Opcional —</option>
                  {failureModes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='risk-effect' className='block text-sm font-medium mb-1'>
                  Efecto
                </label>
                <select
                  id='risk-effect'
                  value={effectId}
                  onChange={(e) => setEffectId(e.target.value)}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                >
                  <option value=''>— Opcional —</option>
                  {effects.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div>
            <label htmlFor='risk-method' className='block text-sm font-medium mb-1'>
              Método de evaluación
            </label>
            <select
              id='risk-method'
              value={evaluationMethodId}
              onChange={(e) => {
                setEvaluationMethodId(e.target.value);
                setFormError(null);
              }}
              required
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            >
              <option value=''>— Seleccionar método —</option>
              {methods.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} {m.isDefault ? '(predeterminado)' : ''}
                </option>
              ))}
            </select>
            {formError && <p className='mt-1 text-sm text-destructive'>{formError}</p>}
          </div>

          <div>
            <label htmlFor='risk-title' className='block text-sm font-medium mb-1'>
              Título
            </label>
            <input
              id='risk-title'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={255}
              placeholder='Ej: Retraso en entregas a clientes'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>

          <div>
            <label htmlFor='risk-description' className='block text-sm font-medium mb-1'>
              Descripción
            </label>
            <textarea
              id='risk-description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              maxLength={10000}
              placeholder='Describa el riesgo u oportunidad…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>

          <div>
            <label htmlFor='risk-process-area' className='block text-sm font-medium mb-1'>
              Área de proceso
            </label>
            <input
              id='risk-process-area'
              value={processArea}
              onChange={(e) => setProcessArea(e.target.value)}
              maxLength={160}
              placeholder='Ej: Logística, Producción…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label htmlFor='risk-source-swot' className='block text-sm font-medium mb-1'>
                Origen FODA
              </label>
              <select
                id='risk-source-swot'
                value={sourceSwotId}
                onChange={(e) => setSourceSwotId(e.target.value)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                <option value=''>— Sin origen FODA —</option>
                {swotOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    [{entry.category}] {entry.description.slice(0, 60)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor='risk-source-party' className='block text-sm font-medium mb-1'>
                Parte interesada
              </label>
              <select
                id='risk-source-party'
                value={sourcePartyId}
                onChange={(e) => setSourcePartyId(e.target.value)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                <option value=''>— Sin parte interesada —</option>
                {partyOptions.map((party) => (
                  <option key={party.id} value={party.id}>
                    {party.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!initial && (
            <fieldset className='rounded-md border border-border p-3 space-y-3'>
              <legend className='text-sm font-medium px-1'>Evaluación inicial (S × O × D)</legend>
              <ScoreRow
                severity={severity}
                occurrence={occurrence}
                detection={detection}
                onSeverity={setSeverity}
                onOccurrence={setOccurrence}
                onDetection={setDetection}
              />
            </fieldset>
          )}

          {!usesSod && (
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label htmlFor='risk-probability' className='block text-sm font-medium mb-1'>
                  Probabilidad (1–5)
                </label>
                <select
                  id='risk-probability'
                  value={probability}
                  onChange={(e) => setProbability(Number(e.target.value))}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                >
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='risk-impact' className='block text-sm font-medium mb-1'>
                  Impacto (1–5)
                </label>
                <select
                  id='risk-impact'
                  value={impact}
                  onChange={(e) => setImpact(Number(e.target.value))}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                >
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {initial && (
            <fieldset className='rounded-md border border-border p-3 space-y-3'>
              <legend className='text-sm font-medium px-1'>Evaluación residual (S × O × D × C)</legend>
              <ScoreRow
                severity={residualSeverity}
                occurrence={residualOccurrence}
                detection={residualDetection}
                onSeverity={setResidualSeverity}
                onOccurrence={setResidualOccurrence}
                onDetection={setResidualDetection}
              />
              <div>
                <label htmlFor='risk-residual-control' className='block text-xs font-medium mb-1'>
                  Control (C)
                </label>
                <select
                  id='risk-residual-control'
                  value={residualControl}
                  onChange={(e) => setResidualControl(Number(e.target.value))}
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <p className='text-xs text-muted-foreground'>
                La evaluación residual se registra desde el detalle del riesgo.
              </p>
            </fieldset>
          )}

          <div className='flex justify-end gap-2 pt-2'>
            <button
              type='button'
              onClick={() => onOpenChange(false)}
              className='px-4 py-2 text-sm rounded-md border'
            >
              Cancelar
            </button>
            <button
              type='submit'
              disabled={submitting}
              className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50'
            >
              {submitting ? 'Guardando…' : initial ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ScoreRow({
  severity,
  occurrence,
  detection,
  onSeverity,
  onOccurrence,
  onDetection,
}: {
  severity: number;
  occurrence: number;
  detection: number;
  onSeverity: (v: number) => void;
  onOccurrence: (v: number) => void;
  onDetection: (v: number) => void;
}) {
  return (
    <div className='grid grid-cols-3 gap-3'>
      <div>
        <label className='block text-xs font-medium mb-1'>Severidad (S)</label>
        <select
          value={severity}
          onChange={(e) => onSeverity(Number(e.target.value))}
          className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
        >
          {SCORE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className='block text-xs font-medium mb-1'>Ocurrencia (O)</label>
        <select
          value={occurrence}
          onChange={(e) => onOccurrence(Number(e.target.value))}
          className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
        >
          {SCORE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className='block text-xs font-medium mb-1'>Detección (D)</label>
        <select
          value={detection}
          onChange={(e) => onDetection(Number(e.target.value))}
          className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
        >
          {SCORE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
